/**
 * Maps MightyKiwi odds (GET /api/MightyKiwi/odds) onto PandaScore matches.
 *
 * The two feeds share NO common identifier (PandaScore match id vs MightyKiwi
 * fixtureId live in different namespaces), so matching is a best-effort heuristic:
 * same game + both team names match (order-insensitive) + start time within a
 * window. This will occasionally miss (name spelling differences) or, in rare
 * cases, mis-pair two same-day fixtures between the same teams.
 */
import type { MightyKiwiEsportOdds } from '@/api/mightykiwi/mightyKiwi.types';
import type { PandaScoreMatch } from '@/api/teams/teams.types';
import { Game } from '@/constants/game.const';
import { getGameFromVideogameSlug } from '@/helpers/videogame.helpers';

/** Acceptable gap between the two feeds' kickoff times (ms). */
const START_TIME_WINDOW_MS = 1000 * 60 * 60 * 6; // 6 hours

/** Lower-case, strip punctuation/whitespace/diacritics for loose name comparison. */
export const normalizeTeamName = (value?: string | null): string =>
  (value ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** Map a MightyKiwi competition/game string to the app's Game enum. */
export const competitionToGame = (odds: MightyKiwiEsportOdds): Game | null => {
  const value = `${odds.competition ?? ''} ${odds.game ?? ''}`.toLowerCase();
  if (value.includes('counter') || value.includes('cs')) {
    return Game.CS2;
  }
  if (value.includes('dota')) {
    return Game.DOTA;
  }
  return null;
};

/** True when two team names refer to the same team (either contains the other). */
const namesMatch = (a: string, b: string): boolean =>
  a.length > 0 && b.length > 0 && (a === b || a.includes(b) || b.includes(a));

/** Parse a feed/api start time ("yyyy-MM-dd HH:mm:ss" or ISO) to epoch ms. */
export const parseStartMs = (value?: string | null): number | null => {
  if (!value) {
    return null;
  }
  const iso = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/** Normalised lookup criteria for matching odds against any match source. */
export type OddsMatchCriteria = {
  /** Game/title filter, or null if unknown (skips the game check). */
  game: Game | null;
  /** Normalised team name/acronym keys (need at least 2). */
  teamKeys: string[];
  /** Kickoff in epoch ms, or null. */
  startMs: number | null;
};

/**
 * Core matcher: find the odds entry that best fits the given criteria.
 * Source-agnostic so PandaScore matches and backoffice tips can both use it.
 * Returns null when no confident pairing exists.
 */
export const findOddsByCriteria = (
  { game, teamKeys, startMs }: OddsMatchCriteria,
  oddsList: MightyKiwiEsportOdds[]
): MightyKiwiEsportOdds | null => {
  if (teamKeys.length < 2) {
    return null;
  }

  const candidates = oddsList.filter((odds) => {
    // 1) same game (when both sides expose it)
    const oddsGame = competitionToGame(odds);
    if (game && oddsGame && game !== oddsGame) {
      return false;
    }
    // 2) both odds teams must each match one of the match's team keys
    const t1 = normalizeTeamName(odds.team1);
    const t2 = normalizeTeamName(odds.team2);
    const t1Found = teamKeys.some((k) => namesMatch(k, t1));
    const t2Found = teamKeys.some((k) => namesMatch(k, t2));
    return t1Found && t2Found;
  });

  if (candidates.length === 0) {
    return null;
  }
  if (candidates.length === 1 || startMs == null) {
    return candidates[0];
  }

  // 3) tie-break by closest kickoff, within the window
  let best: MightyKiwiEsportOdds | null = null;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const odds of candidates) {
    const oddsMs = parseStartMs(odds.startTimeUtc ?? odds.startTime);
    const delta = oddsMs == null ? START_TIME_WINDOW_MS : Math.abs(oddsMs - startMs);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = odds;
    }
  }
  return bestDelta <= START_TIME_WINDOW_MS ? best : candidates[0];
};

const matchTeamKeys = (match: PandaScoreMatch): string[] =>
  match.opponents
    .map((o) => o.opponent)
    .flatMap((t) => [normalizeTeamName(t?.name), normalizeTeamName(t?.acronym)])
    .filter(Boolean);

/**
 * Find the odds entry that best corresponds to a PandaScore match.
 * Returns null when no confident pairing exists.
 */
export const findOddsForMatch = (
  match: PandaScoreMatch,
  oddsList: MightyKiwiEsportOdds[]
): MightyKiwiEsportOdds | null =>
  findOddsByCriteria(
    {
      game: getGameFromVideogameSlug(match.videogame?.slug),
      teamKeys: matchTeamKeys(match),
      startMs: parseStartMs(match.beginAt ?? match.scheduledAt),
    },
    oddsList
  );
